import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  MoreHorizontal,
  Eye,
  Edit,
  CheckCircle,
  XCircle,
  Send,
  Info,
  GitCompare,
  Copy,
  Star,
} from "lucide-react";
import {
  Button,
  Badge,
  Avatar,
  AvatarFallback,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@b2b/ui-components";
import { AdminProduct } from "../../types/product.types";
import type { BaseColumnDef } from "@b2b/ui-components/components/Table/types";

interface CreateColumnsOptions {
  isAdmin: boolean;
  stickySellerActions?: boolean;
  loading: boolean;
  actionsLoading: boolean;
  onViewDetails: (productId: string) => void;
  onEditProduct: (productId: string) => void;
  onApproveProduct: (productId: string) => void;
  onRejectProduct: (productId: string) => void;
  onSubmitForApproval: (productId: string) => void;
  onDeleteProduct: (productId: string) => void;
  onDuplicateProduct: (productId: string) => void;
  onMarkAsActive?: (productId: string) => void;
  onMarkAsInactive?: (productId: string) => void;
  /** Seller edits awaiting admin review (approved listings only) */
  onReviewPendingUpdate?: (productId: string) => void;
  onApprovePendingUpdate?: (productId: string) => void;
  onRejectPendingUpdate?: (productId: string) => void;
  /** Seller: open update audit history */
  onViewSellerUpdateHistory?: (productId: string) => void;
  /** Seller: featured product ids from storefront settings */
  featuredProductIds?: Set<string>;
  onToggleFeatured?: (productId: string) => void;
  featuredToggleId?: string | null;
  statusStyles?: {
    [key: string]: { background: string; textColor: string };
  };
}

/** Seller-only: compact status row + dialog for full rejection reason */
function SellerRejectionReasonTrigger({
  productName,
  reason,
  dialogTitle = "Rejection reason",
  linkLabel = "Tap to view",
}: {
  productName: string;
  reason: string;
  dialogTitle?: string;
  linkLabel?: string;
}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 max-w-[11rem] text-center text-xs font-medium text-primary underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 rounded-sm"
      >
        {linkLabel}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Feedback from the admin for{" "}
            <span className="font-medium text-foreground">{productName}</span>.
          </p>
          <div className="max-h-[min(50vh,280px)] overflow-y-auto rounded-lg border border-border/80 bg-muted/40 px-3 py-3 text-sm leading-relaxed text-foreground whitespace-pre-wrap">
            {reason}
          </div>
          <DialogFooter>
            <Button type="button" variant="primary" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function formatProductDate(value?: string | Date | null): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleDateString();
}

function isProductActive(product: AdminProduct): boolean {
  if (product.planExpired === true || product.isDisabled === true) return false;
  // Product availability toggle in this app is based on isEnabled.
  // Keep isActive as the lowest-priority fallback because it is also used for soft-delete in APIs.
  if (typeof product.isEnabled === "boolean") return product.isEnabled;
  if (typeof product.isActive === "boolean") return product.isActive;
  if (typeof product.enabled === "boolean") return product.enabled;
  if (product.activeStatus) return product.activeStatus.toLowerCase() === "active";
  return true;
}

export function createProductColumns(
  options: CreateColumnsOptions
): ColumnDef<AdminProduct>[] {
  const {
    isAdmin,
    stickySellerActions = true,
    loading,
    actionsLoading,
    onViewDetails,
    onEditProduct,
    onApproveProduct,
    onRejectProduct,
    onSubmitForApproval,
    onDeleteProduct,
    onDuplicateProduct,
    onMarkAsActive,
    onMarkAsInactive,
    onReviewPendingUpdate,
    onApprovePendingUpdate,
    onRejectPendingUpdate,
    onViewSellerUpdateHistory,
    featuredProductIds,
    onToggleFeatured,
    featuredToggleId,
    statusStyles = {
      pending: { background: "bg-yellow-100", textColor: "text-yellow-700" },
      approved: { background: "bg-green-100", textColor: "text-green-700" },
      rejected: { background: "bg-red-100", textColor: "text-red-700" },
      draft: { background: "bg-gray-100", textColor: "text-gray-700" },
    },
  } = options;

  const columns: ColumnDef<AdminProduct>[] = [
    {
      accessorKey: "name",
      header: "Product",
      size: 320,
      cell: ({ row }) => {
        const product = row.original;
        const sellerName = product.assignedSeller
          ? `${product.assignedSeller.firstName} ${product.assignedSeller.lastName}`
          : "Unassigned";

        return (
          <div className="flex items-center space-x-3 min-w-0 max-w-[320px]">
            <Avatar className="h-10 w-10 rounded-lg flex-shrink-0">
              <AvatarFallback className="bg-orange-100 text-orange-500 rounded-lg">
                {product.name
                  .split(" ")
                  .slice(0, 2)
                  .map((n) => n[0])
                  .join("")}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0">
                <span className="font-medium truncate block">
                  {product.name}
                </span>
                {isAdmin && product.hasPendingUpdate && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-amber-100 text-amber-800 flex-shrink-0"
                  >
                    Update pending review
                  </Badge>
                )}
                {isAdmin && product.adminCreated && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-blue-100 text-blue-700 flex-shrink-0"
                    title={
                      sellerName === "Unassigned"
                        ? "Admin Owned"
                        : "Admin Created for Seller"
                    }
                  >
                    {sellerName === "Unassigned"
                      ? "Admin Owned"
                      : "Admin Created"}
                  </Badge>
                )}
                {!isAdmin && product.status === "draft" && (
                  <Badge
                    variant="secondary"
                    className="text-xs bg-gray-100 text-gray-700 flex-shrink-0"
                  >
                    Draft
                  </Badge>
                )}
              </div>
              <div className="text-sm text-muted-foreground truncate">
                SKU: {product.sku}
              </div>
            </div>
          </div>
        );
      },
      enableSorting: true,
      align: "left",
    } as BaseColumnDef<AdminProduct>,
    {
      accessorKey: "assignedSeller",
      header: "Seller",
      size: 120,
      cell: ({ row }) => {
        const product = row.original;
        if (isAdmin) {
          const sellerName = product.assignedSeller
            ? `${product.assignedSeller.firstName} ${product.assignedSeller.lastName}`
            : "Unassigned";
          return (
            <span className="truncate block max-w-[120px]" title={sellerName}>
              {sellerName}
            </span>
          );
        }
        return <span>My Products</span>;
      },
      enableSorting: false,
      align: "left",
    } as BaseColumnDef<AdminProduct>,
    {
      accessorKey: "category",
      header: "Category",
      size: 120,
      cell: ({ row }) => {
        const product = row.original;
        const categoryName =
          product.category || product.categoryId || "Uncategorized";
        return (
          <span className="inline-block max-w-[120px]" title={categoryName}>
            <Badge variant="outline" className="truncate max-w-[120px]">
              {categoryName}
            </Badge>
          </span>
        );
      },
      enableSorting: true,
      align: "left",
    } as BaseColumnDef<AdminProduct>,
    {
      accessorKey: "price",
      header: "Price",
      size: 100,
      cell: ({ row }) => {
        const product = row.original;
        return <span className="font-medium">${product.price.toFixed(2)}</span>;
      },
      enableSorting: true,
      align: "right",
    } as BaseColumnDef<AdminProduct>,
    {
      accessorKey: "approvalStatus",
      header: "Status",
      size: isAdmin ? 100 : 148,
      cell: ({ row }) => {
        const product = row.original;
        const status = product.approvalStatus;
        const style = statusStyles[status] || statusStyles.pending;
        const hasRejectionReason =
          !isAdmin &&
          status === "rejected" &&
          Boolean(product.adminNotes?.trim());

        return (
          <div className="flex flex-col items-center gap-0.5 max-w-[220px] mx-auto py-0.5">
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.background} ${style.textColor}`}
            >
              {status}
            </span>
            {hasRejectionReason ? (
              <SellerRejectionReasonTrigger
                productName={product.name}
                reason={product.adminNotes!.trim()}
              />
            ) : null}
            {!isAdmin && status === "rejected" && !hasRejectionReason ? (
              <span className="mt-1 max-w-[11rem] text-center text-[11px] text-muted-foreground leading-snug">
                No written reason was provided.
              </span>
            ) : null}
          </div>
        );
      },
      enableSorting: true,
      align: "center",
    } as BaseColumnDef<AdminProduct>,
    {
      id: "listingUpdate",
      header: "Latest Status",
      size: 132,
      cell: ({ row }) => {
        const product = row.original;
        const wf = product.sellerUpdateReviewStatus;
        if (!wf) {
          return (
            <span className="text-muted-foreground text-xs block text-center">
              —
            </span>
          );
        }
        const pending =
          Boolean(product.hasPendingUpdate) || wf === "awaiting";
        const declined =
          !product.hasPendingUpdate && wf === "declined";
        const applied = !product.hasPendingUpdate && wf === "applied";

        return (
          <div className="flex flex-col items-center gap-1 max-w-[8.5rem] mx-auto py-0.5">
            {pending ? (
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-900">
                Pending
              </span>
            ) : declined ? (
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                Rejected
              </span>
            ) : applied ? (
              <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                Approved
              </span>
            ) : (
              <span className="text-xs text-muted-foreground">—</span>
            )}
            {declined && product.sellerUpdateRejectionReason?.trim() ? (
              <SellerRejectionReasonTrigger
                productName={product.name}
                reason={product.sellerUpdateRejectionReason.trim()}
                dialogTitle="Update declined"
                linkLabel="Why?"
              />
            ) : null}
            {declined && !product.sellerUpdateRejectionReason?.trim() ? (
              <span className="text-[10px] text-muted-foreground text-center leading-tight">
                No reason on file
              </span>
            ) : null}
            {onViewSellerUpdateHistory ? (
              <button
                type="button"
                onClick={() => onViewSellerUpdateHistory(product.id)}
                className="text-[11px] font-medium text-primary hover:underline"
              >
                History
              </button>
            ) : null}
          </div>
        );
      },
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<AdminProduct>,
    {
      accessorKey: "activeStatus",
      id: "activeStatus",
      header: "Active Status",
      size: 120,
      cell: ({ row }) => {
        const product = row.original;
        const active = isProductActive(product);
        const style = active
          ? { background: "bg-green-100", textColor: "text-green-700" }
          : { background: "bg-gray-100", textColor: "text-gray-700" };
        return (
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${style.background} ${style.textColor}`}
          >
            {active ? "Active" : "Inactive"}
          </span>
        );
      },
      enableSorting: false,
      align: "center",
    } as BaseColumnDef<AdminProduct>,
    {
      accessorKey: "createdAt",
      header: "Created Date",
      size: 110,
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">
          {formatProductDate(row.original.createdAt)}
        </span>
      ),
      enableSorting: true,
      align: "left",
    } as BaseColumnDef<AdminProduct>,
    {
      accessorKey: "updatedAt",
      header: "Updated Date",
      size: 110,
      cell: ({ row }) => (
        <span className="text-sm whitespace-nowrap">
          {formatProductDate(row.original.updatedAt)}
        </span>
      ),
      enableSorting: true,
      align: "left",
    } as BaseColumnDef<AdminProduct>,
    {
      id: "actions",
      header: "Actions",
      size: 80,
      headerClassName: !isAdmin && stickySellerActions
        ? "sticky right-0 z-20 !bg-gray-100 shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.15)]"
        : undefined,
      cellClassName: !isAdmin && stickySellerActions
        ? "sticky right-0 z-10 !bg-white shadow-[-8px_0_8px_-8px_rgba(0,0,0,0.08)]"
        : undefined,
      cell: ({ row }) => {
        const product = row.original;
        return (
          <div className="flex justify-center">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={loading || actionsLoading}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white">
                <DropdownMenuItem onClick={() => onViewDetails(product.id)}>
                  <Eye className="mr-2 h-4 w-4" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEditProduct(product.id)}>
                  <Edit className="mr-2 h-4 w-4" />
                  Edit Product
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDuplicateProduct(product.id)}>
                  <Copy className="mr-2 h-4 w-4" />
                  Duplicate Product
                </DropdownMenuItem>
                {!isAdmin && onToggleFeatured ? (
                  <DropdownMenuItem
                    onClick={() => onToggleFeatured(product.id)}
                    disabled={
                      loading ||
                      actionsLoading ||
                      featuredToggleId === product.id
                    }
                  >
                    <Star
                      className={`mr-2 h-4 w-4 ${
                        featuredProductIds?.has(product.id)
                          ? "fill-[#FF6A00] text-[#FF6A00]"
                          : ""
                      }`}
                    />
                    {featuredProductIds?.has(product.id)
                      ? "Remove from Featured"
                      : "Mark as Featured"}
                  </DropdownMenuItem>
                ) : null}
                {isAdmin &&
                  product.hasPendingUpdate &&
                  onReviewPendingUpdate &&
                  onApprovePendingUpdate &&
                  onRejectPendingUpdate && (
                    <>
                      <DropdownMenuItem
                        onClick={() => onReviewPendingUpdate(product.id)}
                      >
                        <GitCompare className="mr-2 h-4 w-4" />
                        Review changes
                      </DropdownMenuItem>
                      {/* <DropdownMenuItem
                        onClick={() => onApprovePendingUpdate(product.id)}
                      >
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Approve update
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-red-600"
                        onClick={() => onRejectPendingUpdate(product.id)}
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Reject update
                      </DropdownMenuItem> */}
                    </>
                  )}
                {isAdmin && product.approvalStatus === "pending" && (
                  <>
                    <DropdownMenuItem
                      onClick={() => onApproveProduct(product.id)}
                    >
                      <CheckCircle className="mr-2 h-4 w-4" />
                      Approve
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={() => onRejectProduct(product.id)}
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Reject
                    </DropdownMenuItem>
                  </>
                )}
                {!isAdmin && product.status === "draft" && (
                  <DropdownMenuItem
                    onClick={() => onSubmitForApproval(product.id)}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    Submit for Approval
                  </DropdownMenuItem>
                )}
                {!isAdmin && product.approvalStatus === "pending" && (
                  <DropdownMenuItem disabled>
                    <Info className="mr-2 h-4 w-4" />
                    Awaiting Admin Approval
                  </DropdownMenuItem>
                )}
                {isAdmin && onMarkAsActive && onMarkAsInactive && (
                  <>
                    {isProductActive(product) ? (
                      <DropdownMenuItem
                        onClick={() => onMarkAsInactive(product.id)}
                      >
                        <XCircle className="mr-2 h-4 w-4" />
                        Mark as Inactive
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => onMarkAsActive(product.id)}>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Mark as Active
                      </DropdownMenuItem>
                    )}
                  </>
                )}
                <DropdownMenuItem
                  className="text-red-600"
                  onClick={() => onDeleteProduct(product.id)}
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
      enableSorting: false,
      align: "right",
    } as BaseColumnDef<AdminProduct>,
  ];

  return columns;
}
